import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {finalize, map, startWith} from 'rxjs/operators';
import {RetentionService} from '../../../services/retention.service';
import {Policy} from '../../../models/retention.model';
import {DialogService} from '../../../services/dialog.service';
import {ReportingService} from '../../../services/reporting.service';
import {CustomSnackBarService} from '../../../services/custom-snackbar.service';
import {TranslateService} from '@ngx-translate/core';
import {RETENTION_BINDINGS_ALL_BINDINGS_SELECTOR} from '../../../constants/reporting-constants';
import {extractErrorMessage} from '../../../utils/error-utils';


@Component({
  selector: 'app-reports-assignment',
  templateUrl: './reports-assignment.component.html',
  styleUrls: ['./reports-assignment.component.scss']
})
export class ReportsAssignmentComponent implements OnInit {

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  public policies: Policy[] = [];
  public loading$ = this.loadingSubject.asObservable();

  public retentionReportForm: FormGroup;
  public retentionSelectorControl = new FormControl();
  public exportButtonDisabled = false;

  public filteredOptions: Observable<Policy[]>;

  constructor(private readonly retentionService: RetentionService,
              private readonly reportingService: ReportingService,
              private readonly dialogService: DialogService,
              private readonly formBuilder: FormBuilder,
              private readonly customSnackBarService: CustomSnackBarService,
              private readonly translate: TranslateService) {

    this.retentionReportForm = formBuilder.group({
      retention: this.retentionSelectorControl,
    });
  }

  ngOnInit(): void {
    this.loadPoliciesAndInitOptions();
  }

  initFilteredOpt(): void {
    this.filteredOptions = this.retentionSelectorControl.valueChanges
      .pipe(
        startWith(''),

        map(value => {
          return typeof value === 'string' ? value : value.id;
        }),

        map(name => {
          if (!name) {
            this.exportButtonDisabled = false;
          }
          return name ? this._filter(name) : this.policies.slice();
        })
      );
  }

  displayFn(policy: Policy): string {
    return policy?.id ?? '';
  }

  private _filter(name: string): Policy[] {
    const filterValue = name.toLowerCase();
    const filteredPolicies = this.policies.filter(policy => policy.id.toLowerCase().startsWith(filterValue));

    if (filteredPolicies.length === 0 && filterValue) {
      this.exportButtonDisabled = true;
    }

    if (filteredPolicies.length > 0) {
      this.exportButtonDisabled = !this.matchInputWithFilteredPolicies(filterValue, filteredPolicies);
    }
    return filteredPolicies;
  }

  private matchInputWithFilteredPolicies(filterValue: string, policies: Policy[]): boolean {
    const filteredPolicies = policies.filter(policy => policy.id.toLowerCase() === filterValue);
    return filteredPolicies.length > 0;
  }

  loadPoliciesAndInitOptions(): void {
    this.loadingSubject.next(true);
    this.retentionService.listPolicies().pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.policies = resp;
        this.initFilteredOpt();
      },
      error: (error) => {
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  launchReport(): void {
    const retentionCode = this.retentionReportForm.getRawValue();
    const selectedRetention = (retentionCode.retention === null || retentionCode.retention === '') ?
      RETENTION_BINDINGS_ALL_BINDINGS_SELECTOR : retentionCode.retention.id;
    this.reportingService.exportRetentionBindings(selectedRetention).subscribe({
      next: () => {
        this.customSnackBarService.openSuccessSnackbar(this.translate.instant('snackBarMessage.exportStarted'));
        this.retentionReportForm.reset();
        this.exportButtonDisabled = true;
        this.initFilteredOpt();
      },
      error: error => this.dialogService.error(extractErrorMessage(error))
    });
  }

  optionSelected(): void {
    this.exportButtonDisabled = false;
  }

}
